import { useState } from 'react'
import type { ComponentType, ReactNode } from 'react'
import {
  BarChart3,
  FileText,
  Download,
  Play,
  Calendar,
  Clock,
  Users,
  DollarSign,
  FileCheck,
  TrendingUp,
  RefreshCw,
  CheckCircle,
  XCircle,
  MoreVertical
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { KpiGrid } from '@/components/ui/KpiGrid'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useReports } from '@/hooks/useReports'
import { ReportsListTab } from '@/components/reports/ReportsListTab'
import { ReportExecutionModal } from '@/components/reports/ReportExecutionModal'
import { ReportResults } from '@/components/reports/ReportResults'
import type { ReportDefinition, ReportExecutionListItem, ReportResult } from '@/services/reports-service'
import { parseDbTimestamp } from '@/utils/datetime'
import { cn } from '@/utils/cn'

type ReportsTab = 'overview' | 'reports' | 'executions' | 'scheduled'

const tabs: Array<{ id: ReportsTab; label: string; icon: ComponentType<{ className?: string }> }> = [
  { id: 'overview', label: 'Visão Geral', icon: TrendingUp },
  { id: 'reports', label: 'Relatórios', icon: FileText },
  { id: 'executions', label: 'Execuções', icon: Play },
  { id: 'scheduled', label: 'Agendados', icon: Calendar }
]

const categoryIcons: Record<string, ComponentType<{ className?: string }>> = {
  clients: Users,
  finance: DollarSign,
  cases: FileCheck,
  tasks: CheckCircle
}

const executionStatus: Record<ReportExecutionListItem['status'], {
  label: string
  icon: ComponentType<{ className?: string }>
  tone: string
}> = {
  completed: { label: 'Concluído', icon: CheckCircle, tone: 'text-primary' },
  running: { label: 'Executando', icon: RefreshCw, tone: 'text-primary' },
  failed: { label: 'Falhou', icon: XCircle, tone: 'text-destructive' },
  pending: { label: 'Pendente', icon: Clock, tone: 'text-muted-foreground' }
}

function formatDuration(ms: number) {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${Math.round(ms / 1000)}s`
  return `${Math.round(ms / 60000)}min`
}

function formatStartedAt(startedAt: string) {
  return parseDbTimestamp(startedAt)?.toLocaleString() ?? '-'
}

function CategoryIcon({ category }: { category: string }) {
  const Icon = categoryIcons[category] ?? BarChart3
  return <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
}

function ExecutionStatusIcon({ status }: { status: ReportExecutionListItem['status'] }) {
  const { icon: Icon, tone } = executionStatus[status] ?? executionStatus.pending
  return <Icon className={cn('size-4 shrink-0', tone, status === 'running' && 'animate-spin')} aria-hidden="true" />
}

function ExecutionStatusText({ status }: { status: ReportExecutionListItem['status'] }) {
  const { label, tone } = executionStatus[status] ?? executionStatus.pending
  return (
    <div className="flex items-center gap-2">
      <ExecutionStatusIcon status={status} />
      <span className={cn('text-sm', tone)}>{label}</span>
    </div>
  )
}

function StatTile({ label, value, icon: Icon, tone }: {
  label: string
  value: string | number
  icon: ComponentType<{ className?: string }>
  tone?: string
}) {
  return (
    <Card size="sm">
      <CardContent className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className={cn('text-2xl font-semibold tabular-nums', tone)}>{value}</p>
        </div>
        <Icon className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
      </CardContent>
    </Card>
  )
}

function RerunButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button variant="ghost" size="icon-sm" onClick={onClick} aria-label="Executar novamente">
          <RefreshCw className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Executar novamente</TooltipContent>
    </Tooltip>
  )
}

function EmptyState({ icon: Icon, text, children }: {
  icon: ComponentType<{ className?: string }>
  text: string
  children?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center">
      <Icon className="size-10 text-muted-foreground" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">{text}</p>
      {children}
    </div>
  )
}

export function ReportsPage() {
  const {
    reports,
    executions,
    stats,
    loading,
    generateReport,
    exportReport,
    isExecuting,
    refresh
  } = useReports()

  const [activeTab, setActiveTab] = useState<ReportsTab>('overview')
  const [selectedReport, setSelectedReport] = useState<ReportDefinition | null>(null)
  const [showExecuteModal, setShowExecuteModal] = useState(false)
  const [currentResult, setCurrentResult] = useState<ReportResult | null>(null)
  const [showResultsModal, setShowResultsModal] = useState(false)

  const handleExecuteReport = (report: ReportDefinition) => {
    setSelectedReport(report)
    setShowExecuteModal(true)
  }

  const handleExecute = async (
    parameters: Record<string, unknown>,
    action: 'view' | 'export',
    format?: 'csv' | 'json'
  ) => {
    if (!selectedReport) return

    try {
      if (action === 'view') {
        const result = await generateReport({
          reportCode: selectedReport.code,
          parameters
        })

        if (result) {
          setCurrentResult(result)
          setShowResultsModal(true)
        }
      } else if (format) {
        await exportReport(selectedReport.code, format, parameters)
      }

      setShowExecuteModal(false)
    } catch (error) {
      console.error('Error executing report:', error)
      alert('Erro ao executar relatório. Tente novamente.')
    }
  }

  const handleExportFromResults = async (format: 'csv' | 'json') => {
    if (!currentResult || !selectedReport) return

    try {
      await exportReport(selectedReport.code, format, currentResult.metadata.parameters)
    } catch (error) {
      console.error('Error exporting report:', error)
      alert('Erro ao exportar relatório. Tente novamente.')
    }
  }

  const findReport = (execution: ReportExecutionListItem) =>
    reports.find(r => r.code === execution.reportCode)

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando relatórios...</span>
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-10" />
        <KpiGrid cols={4}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}
        </KpiGrid>
        <Skeleton className="h-48" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <BarChart3 className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Relatórios</h1>
          </div>
          <p className="text-sm text-muted-foreground">Gere e gerencie relatórios analíticos do sistema</p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading}>
          <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
          Atualizar
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ReportsTab)} className="gap-4">
        <div className="overflow-x-auto border-b">
          <TabsList variant="line" className="h-auto min-w-max justify-start">
            {tabs.map(tab => (
              <TabsTrigger key={tab.id} value={tab.id} className="gap-2 px-3 py-2.5">
                <tab.icon className="size-4" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <TabsContent value="overview">
          {stats && (
            <div className="flex flex-col gap-4">
              <KpiGrid cols={4}>
                <StatTile label="Total de Relatórios" value={stats.totalReports} icon={FileText} />
                <StatTile label="Execuções este Mês" value={stats.executionsThisMonth} icon={Play} tone="text-primary" />
                <StatTile label="Tempo Médio" value={formatDuration(stats.avgExecutionTime)} icon={Clock} />
                <StatTile label="Armazenamento" value={`${stats.totalStorageMB} MB`} icon={Download} />
              </KpiGrid>

              <Card>
                <CardHeader>
                  <h3 className="text-base font-medium">Execuções Recentes</h3>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  {executions.slice(0, 5).map(execution => {
                    const report = findReport(execution)
                    return (
                      <div key={execution.id} className="flex min-w-0 items-center justify-between gap-3 rounded-lg bg-muted/50 p-3">
                        <div className="flex min-w-0 items-center gap-3">
                          <ExecutionStatusIcon status={execution.status} />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">
                              {execution.reportName || execution.reportCode}
                            </p>
                            <p className="text-xs text-muted-foreground tabular-nums">
                              {formatStartedAt(execution.startedAt)}
                              {execution.durationMs ? ` • ${formatDuration(execution.durationMs)}` : null}
                            </p>
                          </div>
                        </div>
                        {execution.status === 'completed' && report && (
                          <RerunButton onClick={() => handleExecuteReport(report)} />
                        )}
                      </div>
                    )
                  })}
                  {executions.length === 0 && (
                    <p className="py-4 text-center text-sm text-muted-foreground">Nenhuma execução encontrada</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="reports">
          <ReportsListTab
            reports={reports}
            loading={loading}
            onExecute={handleExecuteReport}
            isExecuting={(reportCode) => isExecuting(reportCode)}
          />
        </TabsContent>

        <TabsContent value="executions">
          <Card className="min-w-0">
            <CardHeader className="border-b">
              <h3 className="text-base font-medium">Histórico de Execuções ({executions.length})</h3>
            </CardHeader>
            {executions.length === 0 ? (
              <EmptyState icon={Play} text="Nenhuma execução encontrada" />
            ) : (
              <div className="min-w-0 px-2">
                <Table className="min-w-[720px]">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Relatório</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Gerado em</TableHead>
                      <TableHead>Duração</TableHead>
                      <TableHead>Registros</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {executions.map((execution) => {
                      const report = findReport(execution)
                      return (
                        <TableRow key={execution.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {report && <CategoryIcon category={report.category} />}
                              <div className="min-w-0">
                                <div className="text-sm font-medium">{execution.reportName}</div>
                                <div className="text-xs text-muted-foreground">{execution.reportCode}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell><ExecutionStatusText status={execution.status} /></TableCell>
                          <TableCell className="text-muted-foreground tabular-nums">
                            {formatStartedAt(execution.startedAt)}
                          </TableCell>
                          <TableCell className="text-muted-foreground tabular-nums">
                            {execution.durationMs ? formatDuration(execution.durationMs) : '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground tabular-nums">
                            {execution.totalRecords ?? '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center justify-end gap-1">
                              {execution.status === 'completed' && report && (
                                <RerunButton onClick={() => handleExecuteReport(report)} />
                              )}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button variant="ghost" size="icon-sm" aria-label="Mais opções">
                                    <MoreVertical className="size-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Mais opções</TooltipContent>
                              </Tooltip>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="scheduled">
          <Card>
            <EmptyState icon={Calendar} text="Nenhum relatório agendado encontrado">
              <Button className="mt-2">Criar Agendamento</Button>
            </EmptyState>
          </Card>
        </TabsContent>
      </Tabs>

      {selectedReport && (
        <ReportExecutionModal
          report={selectedReport}
          isOpen={showExecuteModal}
          onClose={() => {
            setShowExecuteModal(false)
            setSelectedReport(null)
          }}
          onExecute={handleExecute}
          isExecuting={isExecuting(selectedReport.code)}
        />
      )}

      {currentResult && (
        <ReportResults
          result={currentResult}
          isOpen={showResultsModal}
          onClose={() => {
            setShowResultsModal(false)
            setCurrentResult(null)
          }}
          onExport={handleExportFromResults}
        />
      )}
    </div>
  )
}
